// Card 67, the faction key (R1568), as the legend card of round 086 card 13
// (R1805, R1806, ui-map-interface.md §17.3.67).
//
// **One legend card, two sections.** The faction key and the heat legend
// are one card in the chart card frame (`frame.ts`): the FACTIONS section
// and the HEAT section, each under a head row that names it, and one
// minimize control, which is the one fold of the card. Minimized, the
// card leaves a 44 px chip with the five border dashes and the heat swatch, so the legend never goes without a
// trace. The HEAT section exists only while a heat mode paints the map. The
// owner struck the LAYERS section of card 13 on 2026-10-09.
//
// **The FACTIONS section** is mockup round 075 card 05: one row per
// banner — the ink, the charge badge, the name and the border dash. It is
// the key of the faction second channel (§11): a reader who cannot tell
// two inks apart learns here which charge and which dash a faction wears.
// With the `pirates` flag on (v2 slot 2), it gains two NPC rows in the
// columns of a faction row (R2831): NPC MONSTERS and NPC PIRATES, each with
// a square of the NPC ink, its side mark (the ragged ring, the swallowtail),
// the name, and at the right end the marks of the five monster kinds
// (R2719) or of the five brotherhoods (R2720).
//
// **The first session keeps the legend open** (R2057): the legend opens
// for the new player in the first session of the device
// (`ui/firstsession.ts`), and from the second session on it starts
// minimized to its chip, one press away (the overlay sets the setting). The card stands in the bottom-right
// stack, over the console where its span would cover the compass rose
// (`stack.ts`).
//
// **The DEPTH and the CURRENT sections** of v2 phase 2 (R2616, round 088
// cards 04 and 07, `deep-legend.ts`) stand after HEAT while the hello
// carries the band field or the current field and the reader shows the
// group DEPTH or CURRENTS of the VIEW panel. The card stands for them too.
// The CURRENTS and DEPTH heat maps (R2870) move their body into the HEAT
// section: the CURRENT body, or the four bands of the depth heat look. The
// DEPTH and the CURRENT sections then do not stand (`deepShown`).
// The tide clock of CURRENT states times of day, so its words change only
// at a tide step: the card answers the reason `tide` of the controller.
//
// **The HEAT section** is the heat legend of card 11: the mode, a CSS
// gradient of its ramp and the two end words. It takes the chart frame but
// keeps the chrome face in both readings (round 086 open point 2), because
// the heat field is a map filter and not chart furniture.
//
// **THE KEY CANNOT DRIFT FROM THE MAP.** The badge is `drawFactionBadge` of
// `render/faction-marks.ts`, the function the atlas bakes the hold, the
// installation and the hull pip with, and the dash is the pattern of
// `factionmarks.toml` the territory composite reads.
//
// **A desktop card.** The card stands on a desktop alone (`mobile = false`):
// a telephone keeps no card at rest (card 08), so its key is a section of
// the VIEW panel (card 09 b, `keyRowsHtml`) and its heat legend the thin
// ramp at the edge of the clear map (card 11 b).
//
// **IT IS A WORLD THEME FIELD, NOT A CHART PIECE TO HIDE.** `chart.toml`
// marks it `off = true`, so it stands under the setting `off` too and no
// Chart theme reaches its gate (R1294). The World field `factionKey` turns
// the FACTIONS section on and off; its AUTO value is on (themes.md §3.4).

import {
  CHART, FACTION_MARKS, FLEET_NPC_MARK_PX, NPC_INK, TERRITORY_BORDER_PASSES,
} from '../../gen/presentation';
import { FACTIONS } from '../../gen/balance';
import { chart as S } from '../../gen/strings';
import { HeatmapKind, ShipClass } from '../../gen/proto/aquaphobia';
import { Canvas2DMarks, type MarkGraphics } from '../../render/marks';
import {
  MONSTER, NPC_BOX, NPC_PATHS, SWALLOWTAIL_EYE, paintPirateHull, paintPirateLight,
  pirateLightOf, pirateLightXs, type PirateInks,
} from '../../render/npc-marks';
import { BROTHERHOOD_WORDS } from '../cards/dens';
import { MONSTER_CLASSES, MONSTER_CLASS_WORDS, kindMarkSvg } from '../../render/monster-marks';
import { dashPattern, dashSample, drawFactionBadge } from '../../render/faction-marks';
import type { HoldEra } from '../../render/hold-marks';
import type { ChartContext, DomPiece } from '../../fast/chart/context';
import { chartCard } from '../../fast/chart/dom';
import { heatMode } from '../../state';
import { rampCss } from '../format';
import { esc } from '../esc';
import { factionName, hex } from '../../world';
import { bannerShowing } from './cartouche';
import { cardHead, minIcon, stepClass } from './frame';
import { restack, stackCard, unstackCard } from './stack';
import { deviceRatio } from '../../render/dpr';
import { rgba } from '../../render/color';
import { strongestCurrent } from '../../deep';
import { currentBodyHtml, deepShown, depthBodyHtml, depthHeatBodyHtml, type DeepShown } from './deep-legend';
import { DEPTH_LOOK_DEFAULT } from '../../depthlook';

/** The figures the card reads out of its own table. */
const T = CHART.pieces.faction_key;

/** The line pass of a border: the last of the three passes (§4.3). */
const LINE_PASS = TERRITORY_BORDER_PASSES[TERRITORY_BORDER_PASSES.length - 1];

/** One row of the key: what it names, and the words it writes. */
export interface KeyRow {
  faction: number;
  /** The faction's name, with the reader's own mark on the reader's row. */
  label: string;
  color: number;
  charge: string;
}

/**
 * The five rows, in the order of the roster.
 *
 * `you` is the reader's faction, 0 for a reader with no seat. A Clan War
 * world names each banner by its clan, which `factionName` answers. `on`
 * drops a faction a custom world switched off (`GameState.factionOn`,
 * R3095); the default keeps all five.
 */
export function keyRows(you: number, on: (faction: number) => boolean = () => true): KeyRow[] {
  return FACTIONS.filter((f) => on(f.id)).map((f) => {
    const name = factionName(f.id);
    return {
      faction: f.id,
      label: f.id === you ? S.faction_key_you({ name }) : name,
      color: f.color,
      charge: f.charge,
    };
  });
}

/** The two sections of the legend card. */
export type LegendSection = 'factions' | 'heat' | 'depth' | 'current';

/**
 * What the overlay states of the card: the FACTION KEY tile (the setting
 * `factionKey` of a desktop) and the minimize control (the setting
 * `legend`). The overlay writes them on each draw of the map buttons, and
 * the card reads them. The card has one fold state: open, or minimized to
 * its chip.
 */
const view = { open: true };
/** The welcome card stands and keeps the legend minimized (R2127). */
let held = false;

/** The overlay's answer for the legend's minimize control. */
export function setLegendOpen(on: boolean): void { view.open = on; }

/**
 * The welcome card rose (R2127, player-reach.md §8.5): the legend stands
 * minimized to its chip while the card is the first frame.
 */
export function holdLegend(): void { held = true; }

/** The welcome card closed, or the player pressed the chip while the card
 *  stood: the legend returns to its own state at once. */
export function releaseLegend(): void { held = false; }

/** Does the welcome card keep the legend minimized? */
export function legendHeld(): boolean { return held; }

/** Is the legend open now: its setting is on and no welcome card holds it? */
function legendOpen(): boolean { return view.open && !held; }

/** The badge canvas side, in px: the shield of the historical reading fits. */
export const BADGE_BOX = Math.ceil(T.fig.badge_px * FACTION_MARKS.shieldAspect) + 2;

/** The badge and dash pictures already painted, by key. */
const pictures = new Map<string, string>();

/** A faction row of the roster. */
type FactionRow = (typeof FACTIONS)[number];

/**
 * One picture of a faction as an `<img>` `w` × `h` px, painted once and
 * kept by `key`. A faction the roster does not hold, and a document with no
 * 2D canvas (a test page), get ''.
 */
function factionPicture(
  key: string, faction: number, w: number, h: number, cls: string,
  draw: (m: Canvas2DMarks, f: FactionRow) => void,
): string {
  const hit = pictures.get(key);
  if (hit !== undefined) return hit;
  const f = FACTIONS.find((r) => r.id === faction);
  let out = '';
  if (f && typeof document !== 'undefined') {
    const c = canvasOf(w, h);
    if (paint(c, (m) => draw(m, f))) {
      out = `<img${cls} src="${c.toDataURL('image/png')}" width="${w}" height="${h}" alt="">`;
    }
  }
  pictures.set(key, out);
  return out;
}

/**
 * The badge of one faction as an `<img>` at `px` (R1757): the badge of a
 * key row, painted once through the same `drawFactionBadge` and kept as an
 * image URL, so a chrome string that carries it stays stable. The commander
 * row of the OPTIONS menu and the key section of the VIEW panel draw it. A
 * faction the roster does not hold, and a document with no 2D canvas (a
 * test page), get ''.
 */
export function badgePicture(faction: number, era: HoldEra, px: number): string {
  const box = Math.ceil(px * FACTION_MARKS.shieldAspect) + 2;
  return factionPicture(`${faction}:${era}:${px}`, faction, box, box, '',
    (m, f) => drawFactionBadge(m, era, f.charge, f.color, box / 2, box / 2, px));
}

/** The border dash of one faction as an `<img>` `w` px long, painted once,
 *  or '' on a page with no 2D canvas. The chip and the VIEW key draw it. */
function dashPicture(faction: number, w: number): string {
  return factionPicture(`dash:${faction}:${w}`, faction, w, BADGE_BOX, ' class="fk-dashimg"',
    (m, f) => dashRow(m, f.id, f.color, w, BADGE_BOX / 2));
}

/** The two NPC rows of the FACTIONS section (R2831). */
export type NpcSide = 'monsters' | 'pirates';

/** The side mark of a row, in the badge column (round 088 card 03 a): the
 *  ragged ring with six arms of a monster, or the swallowtail hull of a
 *  pirate group. The stylesheet draws the ring in `--npc` with the dark
 *  core `--npc-edge`, and the swallowtail as the inverse hull (R2874): the
 *  deep body with the rim and the eye in `--npc`. The geometry is
 *  `render/npc-marks.ts`, which the atlas bakes too. */
function sideMarkSvg(side: NpcSide): string {
  const open = `<svg class="npc-mark" viewBox="0 0 ${NPC_BOX} ${NPC_BOX}" width="14" height="14">`;
  if (side === 'pirates') {
    const e = SWALLOWTAIL_EYE;
    return `${open}<path class="npc-body" d="${NPC_PATHS.swallowtail}"/>`
      + `<circle class="npc-eye" cx="${e.x}" cy="${e.y}" r="${e.r}"/></svg>`;
  }
  const m = MONSTER.eye;
  return `${open}<path class="npc-line" d="${NPC_PATHS.arms}"/><path class="npc-ring" d="${NPC_PATHS.ring}"/>`
    + `<circle class="npc-fill" cx="${m.x}" cy="${m.y}" r="${m.r}"/></svg>`;
}

/** The brotherhoods of `[[npc.brotherhoods]]` in the order of the wire:
 *  the keys of the one word table. */
const BROTHERHOODS: readonly number[] = Object.keys(BROTHERHOOD_WORDS).map(Number).sort((a, b) => a - b);

/** A canvas surface that keeps the x span of what it is handed: it
 *  measures a pirate mark. It answers the calls of `paintPirateHull` and
 *  `paintPirateLight` and paints nothing. A clip does not shrink the span. */
class SpanSurface {
  x0 = Infinity;
  x1 = -Infinity;
  lineWidth = 0;
  strokeStyle = '';
  fillStyle: unknown = '';
  lineJoin = '';
  lineCap = '';
  private dx = 0;
  private stack: number[] = [];
  private see(x: number, r = 0): void {
    this.x0 = Math.min(this.x0, this.dx + x - r);
    this.x1 = Math.max(this.x1, this.dx + x + r);
  }
  moveTo(x: number): void { this.see(x); }
  lineTo(x: number): void { this.see(x); }
  quadraticCurveTo(cx: number, _cy: number, x: number): void { this.see(cx); this.see(x); }
  arc(x: number, _y: number, r: number): void { this.see(x, r); }
  translate(x: number): void { this.dx += x; }
  save(): void { this.stack.push(this.dx); }
  restore(): void { this.dx = this.stack.pop() ?? 0; }
  createRadialGradient(): { addColorStop(): void } { return { addColorStop() { /* no paint */ } }; }
  beginPath(): void { /* no paint */ }
  closePath(): void { /* no paint */ }
  stroke(): void { /* no paint */ }
  fill(): void { /* no paint */ }
  clip(): void { /* no paint */ }
}

/** The inks of a brotherhood mark: the NPC ink and the deep ink of the
 *  inverse hull, the inks the fleet layer tints the baked cell in (R2637,
 *  R2874). */
const PIRATE_INKS: PirateInks = {
  light: (a) => rgba(NPC_INK.color, a),
  deep: (a) => rgba(NPC_INK.edge, a),
};

/** The class a legend mark stands on: none, so each brotherhood mark sits
 *  on the swallowtail of the side mark (R412). */
const NO_CLASS = ShipClass.SHIP_CLASS_UNSPECIFIED;

/** One brotherhood mark through a surface: the hull with its mark, then
 *  each light at full alpha. */
function paintBrotherhood(c: CanvasRenderingContext2D, bro: number): void {
  paintPirateHull(c, NO_CLASS, bro, PIRATE_INKS);
  const light = pirateLightOf(bro);
  if (light === null) return;
  for (const x of pirateLightXs(bro, NO_CLASS)) {
    c.save();
    c.translate(x, 0);
    paintPirateLight(c, light, PIRATE_INKS);
    c.restore();
  }
}

/** The x span of the five brotherhood marks in map px, with a pad for the
 *  stroke widths. Every mark takes the same window, so the five hulls
 *  stand at one size and at one place. */
let broSpan: readonly [number, number] | null = null;
function brotherhoodSpan(): readonly [number, number] {
  if (broSpan === null) {
    const s = new SpanSurface();
    for (const b of BROTHERHOODS) paintBrotherhood(s as unknown as CanvasRenderingContext2D, b);
    broSpan = [s.x0 - 1.5, s.x1 + 1.5];
  }
  return broSpan;
}

/** The width of one brotherhood picture in px of the card: the window of
 *  the five marks at the legend scale. */
function brotherhoodWidth(): number {
  const [x0, x1] = brotherhoodSpan();
  return Math.ceil((x1 - x0) * (T.fig.npc_mark_px / FLEET_NPC_MARK_PX));
}

/**
 * The mark of one brotherhood as an `<img>`, painted once (rounds 111 and
 * 113, R2720): the swallowtail with the mark of the brotherhood and its
 * lights, at the scale of the kind marks (`npc_mark_px` for the 26 px of
 * the map mark). The picture is `npc_mark_px` tall and as wide as the
 * window of the five marks. A document with no 2D canvas (a test page)
 * gets ''.
 */
function brotherhoodPicture(bro: number): string {
  const px = T.fig.npc_mark_px;
  const key = `bro:${bro}:${px}`;
  const hit = pictures.get(key);
  if (hit !== undefined) return hit;
  const [x0] = brotherhoodSpan();
  const k = px / FLEET_NPC_MARK_PX;
  const w = brotherhoodWidth(), h = px;
  let out = '';
  if (typeof document !== 'undefined') {
    const c = canvasOf(w, h);
    const g = c.getContext('2d');
    if (g) {
      const d = c.width / w;
      g.setTransform(d * k, 0, 0, d * k, -d * x0 * k, (d * h) / 2);
      paintBrotherhood(g, bro);
      out = `<img src="${c.toDataURL('image/png')}" width="${w}" height="${h}" alt="">`;
    }
  }
  pictures.set(key, out);
  return out;
}

/** The five marks at the right end of an NPC row, each in a cell that
 *  names it in its title: the kind marks of round 109 (R2719), or the
 *  brotherhood marks of round 113 (R2720). Every cell of both rows is as
 *  wide as a brotherhood picture, so each kind mark stands centred over
 *  the pirate mark under it. */
function sideSetHtml(side: NpcSide): string {
  const w = brotherhoodWidth();
  const cell = (word: string, mark: string) =>
    `<span class="npc-cell" style="width:${w}px" title="${esc(word)}">${mark}</span>`;
  return side === 'monsters'
    ? MONSTER_CLASSES.map((_, k) => cell(MONSTER_CLASS_WORDS[k], kindMarkSvg(k, T.fig.npc_mark_px))).join('')
    : BROTHERHOODS.map((b) => cell(BROTHERHOOD_WORDS[b], brotherhoodPicture(b))).join('');
}

/**
 * One NPC row (R2831), in the columns of a faction row: a square of the
 * NPC ink in the place of the faction ink, the side mark centred in a cell
 * as wide as the badge column, the name at the x of the faction names, and
 * the five marks at the right end, in the place of the border dash. The
 * card writes the columns of its grid (`card`). The VIEW panel key of a
 * telephone keeps its own flex row, whose ink square and badge column have
 * the same widths.
 */
function npcRowHtml(side: NpcSide, card: boolean): string {
  const f = T.fig;
  const row = card
    ? ` ${stepClass(T.text.row)}" style="grid-template-columns:${f.swatch_px}px ${BADGE_BOX}px 1fr auto;`
      + `column-gap:${f.gap_px}px;min-height:${f.row_px}px"`
    : '"';
  const ink = card ? ` style="width:${f.swatch_px}px;height:${f.swatch_px}px;opacity:${f.swatch_alpha}"` : '';
  return `<div class="fk-row fk-npc${row} data-npc="${side}">`
    + `<span class="fk-ink npc-sq"${ink}></span>`
    + `<span class="npc-cell" style="width:${BADGE_BOX}px" aria-hidden="true">${sideMarkSvg(side)}</span>`
    + `<span class="fk-name">${esc(side === 'monsters' ? S.legend_npc_monsters : S.legend_npc_pirates)}</span>`
    + `<span class="npc-set" style="gap:${f.npc_mark_gap_px}px">${sideSetHtml(side)}</span></div>`;
}

/** The two NPC rows, the monsters first, while `npc` holds; '' without. */
function npcRowsHtml(npc: boolean, card: boolean): string {
  return npc ? npcRowHtml('monsters', card) + npcRowHtml('pirates', card) : '';
}

/**
 * The key rows as markup, for the key section of the VIEW panel on a
 * telephone (round 086 card 09 b): the ink, the badge, the name and the
 * dash, and the two NPC rows while `npc` holds. The pictures are the
 * card's own, painted once.
 */
export function keyRowsHtml(you: number, era: HoldEra, npc: boolean,
  on: (faction: number) => boolean = () => true): string {
  const f = T.fig;
  return keyRows(you, on).map((r) => `<div class="fk-row" data-faction="${r.faction}">`
    + `<span class="fk-ink" style="background:${hex(r.color)}"></span>`
    + badgePicture(r.faction, era, f.badge_px)
    + `<span class="fk-name">${esc(r.label)}</span>${dashPicture(r.faction, f.dash_px)}</div>`).join('')
    + npcRowsHtml(npc, false);
}

export class FactionKey implements DomPiece {
  readonly id = 'faction_key' as const;
  readonly tier = 'dom' as const;
  /** A replay film hides the territory, and the key with it (R531). */
  readonly film = 'hide' as const;
  readonly rebuildOn = ['hello', 'reading', 'look', 'layout', 'resize', 'heat', 'settings', 'tide', 'survey'] as const;

  private el: HTMLElement | null = null;
  /** What the card was last written for. */
  private lastSig = '';
  /** The context of the last render, for a refresh the overlay asks for. */
  private lastCtx: ChartContext | null = null;

  /**
   * The card stands while it has a section to show: the FACTIONS section
   * (the World field `factionKey` and the FACTION KEY tile) or the HEAT
   * section (a heat mode paints). It waits for the cartouche: while the
   * season banner holds the map, both cards stay off the glass.
   */
  visible(ctx: ChartContext): boolean {
    if (bannerShowing() || ctx.state.hello === null) return false;
    const deep = deepOf(ctx);
    return factionsShown(ctx) || heatShown(ctx) || deep.depth !== null || deep.current;
  }

  mount(root: HTMLElement, ctx: ChartContext): void {
    const el = chartCard('legend faction-key');
    root.appendChild(el);
    this.el = el;
    this.lastSig = '';
    this.lastCtx = ctx;
  }

  render(ctx: ChartContext): void {
    const el = this.el;
    if (!el) return;
    this.lastCtx = ctx;
    const hist = ctx.reading === 'historical';
    // The badges are drawn in the reading the holds wear, which is the
    // modern one under the setting `off` (§6.2).
    const era: HoldEra = ctx.scene.markEra ?? (hist ? 'h' : 'm');
    const rows = keyRows(ctx.state.faction, (f) => ctx.state.factionOn(f));
    const facs = factionsShown(ctx);
    const npc = facs && ctx.state.hasFeatureFlag('pirates');
    const kind = heatShown(ctx) ? ctx.state.heatmap : HeatmapKind.HEATMAP_KIND_NONE;
    const deep = deepSections(ctx, hist);
    const sig = [hist, era, rows.map((r) => r.label).join('|'), facs, npc, kind,
      legendOpen(), deep.depth, deep.current, deep.heat].join(';');
    if (sig !== this.lastSig) {
      this.lastSig = sig;
      el.classList.toggle('historical', hist);
      el.classList.toggle('chip', !legendOpen());
      el.textContent = '';
      if (legendOpen()) this.write(el, hist, era, facs ? rows : null, npc, kind, deep);
      else this.writeChip(el, hist, facs ? rows : null, kind);
    }
    // The stack folds the open card to its head row while the column would
    // reach the notice band (round 086 card 08).
    stackCard('faction_key', el, (on) => el.classList.toggle('folded', on && legendOpen()));
  }

  /** Writes the card again with the last context: the overlay changed a
   *  setting or a fold, and the card answers at once. */
  refresh(): void {
    if (this.el && this.lastCtx) {
      this.render(this.lastCtx);
      restack();
    }
  }

  unmount(): void {
    this.el?.remove();
    this.el = null;
    unstackCard('faction_key');
  }

  /** The open card: the head, then each section that has a body. */
  private write(
    el: HTMLElement, hist: boolean, era: HoldEra, rows: readonly KeyRow[] | null,
    npc: boolean, kind: HeatmapKind, deep: DeepSections,
  ): void {
    // The head row of the chart card frame (round 086 card 04), with its
    // one minimize control. The overlay's delegate reads `data-act` off
    // the furniture host.
    el.appendChild(cardHead(hist ? S.legend_card_title_historical : S.legend_card_title,
      T.text.title, { act: 'legend', title: S.legend_min_title, folded: false }));
    if (rows) {
      const sec = section('factions', hist ? S.legend_factions_historical : S.faction_key_title);
      for (const r of rows) sec.appendChild(this.row(era, r));
      if (npc) sec.insertAdjacentHTML('beforeend', npcRowsHtml(true, true));
      el.appendChild(sec);
    }
    if (kind !== HeatmapKind.HEATMAP_KIND_NONE) {
      const m = heatMode(kind);
      // The chrome face in both readings (round 086 open point 2).
      const sec = section('heat', S.legend_heat({ mode: m.label }));
      sec.classList.add('chrome-face');
      // A mode the client draws shows the body of its field (R2870).
      sec.insertAdjacentHTML('beforeend', deep.heat !== '' ? deep.heat
        : `<div class="lg-ramp" style="background:${rampCss(m)}"></div>`
        + `<div class="lg-ends cs-note"><span>${esc(m.lo)}</span><span>${esc(m.hi)}</span></div>`);
      el.appendChild(sec);
    }
    if (deep.depth !== '') {
      const sec = section('depth', hist ? S.legend_depth_historical : S.legend_depth);
      sec.insertAdjacentHTML('beforeend', deep.depth);
      el.appendChild(sec);
    }
    if (deep.current !== '') {
      const sec = section('current', hist ? S.legend_current_historical : S.legend_current);
      sec.insertAdjacentHTML('beforeend', deep.current);
      el.appendChild(sec);
    }
  }

  /** The chip of a minimized card: LEGEND, the five dashes, the heat
   *  swatch, and the control that opens the card again (card 13). The
   *  control wears the square press of the folded cartouche plate.
   *  `facs` is the key rows the dashes stand for, or null for none. */
  private writeChip(el: HTMLElement, hist: boolean, facs: KeyRow[] | null, kind: HeatmapKind): void {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'lg-chip';
    b.dataset.act = 'legend';
    b.title = S.legend_open_title;
    b.setAttribute('aria-label', S.legend_open_title);
    b.setAttribute('aria-expanded', 'false');
    const swatch = kind === HeatmapKind.HEATMAP_KIND_NONE ? ''
      : `<i class="lg-swatch" style="background:${rampCss(heatMode(kind))}"></i>`;
    b.innerHTML = `<span class="${stepClass(T.text.title)}">${esc(hist ? S.legend_card_title_historical : S.legend_card_title)}</span>`
      + (facs !== null ? facs.map((r) => dashPicture(r.faction, T.fig.chip_dash_px)).join('') : '')
      + swatch
      + `<span class="cc-min lg-up" aria-hidden="true">${minIcon(true)}</span>`;
    el.appendChild(b);
  }

  /** One faction row: the ink, the badge, the name and the dash. */
  private row(era: HoldEra, r: KeyRow): HTMLElement {
    const f = T.fig;
    const row = document.createElement('div');
    row.className = `fk-row ${stepClass(T.text.row)}`;
    row.dataset.faction = String(r.faction);
    row.style.gridTemplateColumns = `${f.swatch_px}px ${BADGE_BOX}px 1fr ${f.dash_px}px`;
    row.style.columnGap = `${f.gap_px}px`;
    row.style.minHeight = `${f.row_px}px`;

    const ink = document.createElement('span');
    ink.className = 'fk-ink';
    ink.style.width = ink.style.height = `${f.swatch_px}px`;
    ink.style.background = hex(r.color);
    ink.style.opacity = String(f.swatch_alpha);

    const badge = canvasOf(BADGE_BOX, BADGE_BOX);
    badge.className = 'fk-badge';
    paint(badge, (m) => drawFactionBadge(
      m, era, r.charge, r.color, BADGE_BOX / 2, BADGE_BOX / 2, f.badge_px));

    const name = document.createElement('span');
    name.className = 'fk-name';
    name.textContent = r.label;

    const dash = canvasOf(f.dash_px, BADGE_BOX);
    dash.className = 'fk-dash';
    paint(dash, (m) => dashRow(m, r.faction, r.color, f.dash_px, BADGE_BOX / 2));

    row.append(ink, badge, name, dash);
    return row;
  }
}

/** Is the FACTIONS section on: the World field draws the key? */
function factionsShown(ctx: ChartContext): boolean {
  return (ctx.scene.factionKey ?? 'key') === 'key';
}

/** Which deep sections stand for this context (R2616, R2870). */
function deepOf(ctx: ChartContext): DeepShown {
  const s = ctx.state;
  return deepShown(s.heatmap, s.depthBands !== null, s.currents !== null, ctx.hiddenGroups);
}

/** The markup of the two sections of v2 phase 2, '' for a section that
 *  does not stand, and the body of the HEAT section of a mode the client
 *  draws (R2870), '' for every other mode. */
interface DeepSections { depth: string; current: string; heat: string }

/** The sections of v2 phase 2 for this context (R2616, R2870). */
function deepSections(ctx: ChartContext, hist: boolean): DeepSections {
  const shown = deepOf(ctx);
  let current = '';
  if (shown.current) {
    const s = ctx.state;
    current = currentBodyHtml(hist, {
      strongestPxH: strongestCurrent(s.currents, s.rules.currentUnitPxH),
      tide: s.tideNow(), steps: s.rules.tideSteps, clockSec: ctx.worldClockS, tacticalScale: s.tacticalScale,
    });
  }
  // The DEPTH heat map shows the bands of the depth heat look in its HEAT
  // section, and the CURRENTS heat map the current body.
  if (shown.depth === 'heat') return { depth: '', current: '', heat: depthHeatBodyHtml(hist) };
  if (ctx.state.heatmap === HeatmapKind.HEATMAP_KIND_CURRENTS) return { depth: '', current: '', heat: current };
  // The swatches show the depth look the map draws (R2726).
  const depth = shown.depth === 'tints' ? depthBodyHtml(hist, ctx.scene.depthLook ?? DEPTH_LOOK_DEFAULT) : '';
  return { depth, current, heat: '' };
}

/** Does a heat mode paint the map, so the HEAT section stands? */
function heatShown(ctx: ChartContext): boolean {
  return ctx.state.heatmap !== HeatmapKind.HEATMAP_KIND_NONE;
}

/** One section: its head row names it and does not fold it. */
function section(sec: LegendSection, title: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'lg-sec';
  el.dataset.sec = sec;
  const head = document.createElement('div');
  head.className = 'lg-sh';
  head.innerHTML = `<span class="${stepClass(T.text.title)}">${esc(title)}</span>`;
  el.appendChild(head);
  return el;
}

/** A canvas of `w` × `h` px at the device's pixel ratio. */
function canvasOf(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  const dpr = Math.max(1, deviceRatio());
  c.width = Math.round(w * dpr);
  c.height = Math.round(h * dpr);
  c.style.width = `${w}px`;
  c.style.height = `${h}px`;
  return c;
}

/**
 * Paints one canvas through the mark surface, in px of the card.
 *
 * jsdom hands back no 2D context and a browser may refuse one, so a canvas
 * that cannot be painted stays blank and the words still read. The answer
 * says whether the canvas was painted.
 */
function paint(c: HTMLCanvasElement, draw: (m: Canvas2DMarks) => void): boolean {
  const ctx = c.getContext('2d');
  if (!ctx) return false;
  const k = c.width / parseFloat(c.style.width);
  ctx.setTransform(k, 0, 0, k, 0, 0);
  draw(new Canvas2DMarks(ctx));
  return true;
}

/**
 * The border dash of one faction, as a straight sample `w` long: the runs
 * of `dashSample`, each a round-capped stroke of the line pass. The caps
 * stand inside the sample, so it is inset by half the line width.
 */
function dashRow(
  g: MarkGraphics, faction: number, color: number, w: number, y: number,
): void {
  const lw = LINE_PASS.width;
  const runs = dashSample(dashPattern(faction), w - lw);
  for (const [a, b] of runs) {
    g.moveTo(lw / 2 + a, y).lineTo(lw / 2 + b, y)
      .stroke({ width: lw, color, alpha: LINE_PASS.alpha, cap: 'round' });
  }
}

/** The one instance the registry holds. */
export const factionKey = new FactionKey();
